import React, { useState, useEffect, useCallback } from 'react';
import api from '../services/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Checkbox } from '../components/ui/checkbox';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle
} from '../components/ui/dialog';
import { toast } from 'sonner';
import PageHelpButton from '../components/help/PageHelpButton';
import {
  CalendarDays, Plus, ChevronLeft, ChevronRight, Clock, MapPin,
  Trash2, Edit2, Check, X, Loader2, AlertTriangle
} from 'lucide-react';

const CATEGORIES = [
  { value: 'inspeccion_trabajo', label: 'Inspección de Trabajo', color: 'bg-red-500' },
  { value: 'juicio', label: 'Juicio', color: 'bg-purple-500' },
  { value: 'reunion', label: 'Reunión', color: 'bg-blue-500' },
  { value: 'vencimiento_fiscal', label: 'Vencimiento Fiscal', color: 'bg-amber-500' },
  { value: 'smac', label: 'SMAC', color: 'bg-orange-500' },
  { value: 'otro', label: 'Otro', color: 'bg-stone-500' },
];

const getCategoryInfo = (cat) => CATEGORIES.find(c => c.value === cat) || CATEGORIES[5];

const MONTHS = ['Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio', 'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre'];
const DAYS = ['Lun', 'Mar', 'Mié', 'Jue', 'Vie', 'Sáb', 'Dom'];

const emptyForm = { title: '', description: '', category: 'otro', date: '', time: '', location: '', reminder: true };

const CalendarPage = () => {
  const today = new Date();
  const [currentMonth, setCurrentMonth] = useState(today.getMonth());
  const [currentYear, setCurrentYear] = useState(today.getFullYear());
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [editingEvent, setEditingEvent] = useState(null);
  const [selectedDay, setSelectedDay] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);

  const fetchEvents = useCallback(async () => {
    try {
      const { data } = await api.get('/calendar/events', { params: { month: currentMonth + 1, year: currentYear } });
      setEvents(data);
    } catch { toast.error('Error al cargar eventos'); }
    finally { setLoading(false); }
  }, [currentMonth, currentYear]);

  useEffect(() => { fetchEvents(); }, [fetchEvents]);

  const getDaysInMonth = (month, year) => new Date(year, month + 1, 0).getDate();
  const getFirstDayOfMonth = (month, year) => { const d = new Date(year, month, 1).getDay(); return d === 0 ? 6 : d - 1; };

  const prevMonth = () => {
    if (currentMonth === 0) { setCurrentMonth(11); setCurrentYear(y => y - 1); }
    else setCurrentMonth(m => m - 1);
  };
  const nextMonth = () => {
    if (currentMonth === 11) { setCurrentMonth(0); setCurrentYear(y => y + 1); }
    else setCurrentMonth(m => m + 1);
  };
  const goToday = () => { setCurrentMonth(today.getMonth()); setCurrentYear(today.getFullYear()); };

  const openNew = (dateStr) => {
    setEditingEvent(null);
    setForm({ ...emptyForm, date: dateStr });
    setModalOpen(true);
  };

  const openEdit = (event) => {
    setEditingEvent(event);
    setForm({
      title: event.title, description: event.description || '', category: event.category,
      date: event.date, time: event.time || '', location: event.location || '', reminder: event.reminder !== false
    });
    setModalOpen(true);
  };

  const handleSave = async () => {
    if (!form.title.trim() || !form.date) { toast.error('Título y fecha son obligatorios'); return; }
    setSaving(true);
    try {
      if (editingEvent) {
        await api.put(`/calendar/events/${editingEvent.id}`, form);
        toast.success('Evento actualizado');
      } else {
        await api.post('/calendar/events', form);
        toast.success('Evento creado');
      }
      setModalOpen(false);
      fetchEvents();
    } catch { toast.error('Error al guardar evento'); }
    setSaving(false);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('¿Eliminar este evento?')) return;
    try {
      await api.delete(`/calendar/events/${id}`);
      toast.success('Evento eliminado');
      setSelectedDay(null);
      fetchEvents();
    } catch { toast.error('Error al eliminar'); }
  };

  const toggleComplete = async (event) => {
    try {
      await api.put(`/calendar/events/${event.id}`, { completed: !event.completed });
      fetchEvents();
    } catch { toast.error('Error al actualizar'); }
  };

  // Build calendar grid
  const daysInMonth = getDaysInMonth(currentMonth, currentYear);
  const firstDay = getFirstDayOfMonth(currentMonth, currentYear);
  const totalCells = Math.ceil((firstDay + daysInMonth) / 7) * 7;

  const eventsByDate = {};
  events.forEach(e => {
    if (!eventsByDate[e.date]) eventsByDate[e.date] = [];
    eventsByDate[e.date].push(e);
  });

  const todayStr = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}-${String(today.getDate()).padStart(2, '0')}`;
  const selectedDateStr = selectedDay ? `${currentYear}-${String(currentMonth + 1).padStart(2, '0')}-${String(selectedDay).padStart(2, '0')}` : null;
  const selectedEvents = selectedDateStr ? (eventsByDate[selectedDateStr] || []) : [];

  if (loading) {
    return <div className="flex items-center justify-center h-64"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#FF6B4A]"></div></div>;
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div className="flex items-center gap-3">
          <div>
            <h1 className="text-2xl sm:text-3xl font-heading font-extrabold tracking-tight" data-testid="calendar-title">Calendario</h1>
            <p className="text-stone-500 text-sm mt-1">Gestiona tus eventos, inspecciones y citas</p>
          </div>
          <PageHelpButton moduleKey="calendar" variant="icon" />
        </div>
        <Button className="bg-[#FF6B4A] hover:bg-[#E05A3E] rounded-full shadow-lg shadow-orange-200/50 hover:-translate-y-0.5 transition-all duration-300" onClick={() => openNew(todayStr)} data-testid="new-event-button">
          <Plus className="w-4 h-4 mr-2" /> Nuevo Evento
        </Button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Calendar Grid */}
        <div className="lg:col-span-2 bg-white rounded-3xl shadow-[0_8px_30px_rgb(0,0,0,0.04)] border border-stone-100 overflow-hidden">
          {/* Month Navigation */}
          <div className="flex items-center justify-between px-4 sm:px-6 py-4 border-b border-stone-100">
            <button onClick={prevMonth} className="p-2 hover:bg-stone-100 rounded-xl" data-testid="prev-month"><ChevronLeft className="w-5 h-5" /></button>
            <div className="flex items-center gap-3">
              <h2 className="text-lg font-bold" data-testid="current-month">{MONTHS[currentMonth]} {currentYear}</h2>
              <button onClick={goToday} className="text-xs text-[#FF6B4A] font-medium hover:underline">Hoy</button>
            </div>
            <button onClick={nextMonth} className="p-2 hover:bg-stone-100 rounded-xl" data-testid="next-month"><ChevronRight className="w-5 h-5" /></button>
          </div>

          {/* Day Headers */}
          <div className="grid grid-cols-7 border-b border-stone-100">
            {DAYS.map(d => (
              <div key={d} className="px-1 py-2 text-center text-xs font-medium text-stone-500 uppercase">{d}</div>
            ))}
          </div>

          {/* Calendar Cells */}
          <div className="grid grid-cols-7">
            {Array.from({ length: totalCells }, (_, i) => {
              const day = i - firstDay + 1;
              const isValid = day >= 1 && day <= daysInMonth;
              const dateStr = isValid ? `${currentYear}-${String(currentMonth + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}` : null;
              const dayEvents = dateStr ? (eventsByDate[dateStr] || []) : [];
              const isToday = dateStr === todayStr;
              const isSelected = day === selectedDay;

              return (
                <div
                  key={`cell-${i}`}
                  className={`min-h-[80px] sm:min-h-[100px] border-b border-r border-stone-100 p-1 cursor-pointer transition-colors
                    ${!isValid ? 'bg-stone-50' : isSelected ? 'bg-[#FF6B4A]/5' : 'hover:bg-[#FFF7ED]'}
                  `}
                  onClick={() => isValid && setSelectedDay(day)}
                  data-testid={isValid ? `calendar-day-${day}` : undefined}
                >
                  {isValid && (
                    <>
                      <div className="flex items-center justify-between px-1">
                        <span className={`text-sm font-medium ${isToday ? 'bg-[#FF6B4A] text-white w-7 h-7 rounded-full flex items-center justify-center' : 'text-stone-700'}`}>
                          {day}
                        </span>
                        {dayEvents.length > 0 && (
                          <span className="text-[10px] font-medium text-stone-400">{dayEvents.length}</span>
                        )}
                      </div>
                      <div className="mt-1 space-y-0.5">
                        {dayEvents.slice(0, 3).map(ev => {
                          const cat = getCategoryInfo(ev.category);
                          return (
                            <div
                              key={ev.id}
                              className={`text-[10px] sm:text-xs px-1.5 py-0.5 rounded truncate ${ev.completed ? 'line-through opacity-50' : ''}`}
                              style={{ backgroundColor: `${cat.color.replace('bg-', '')}15` }}
                              title={ev.title}
                            >
                              <span className={`inline-block w-1.5 h-1.5 rounded-full ${cat.color} mr-1`}></span>
                              {ev.title}
                            </div>
                          );
                        })}
                        {dayEvents.length > 3 && (
                          <div className="text-[10px] text-stone-400 px-1">+{dayEvents.length - 3} más</div>
                        )}
                      </div>
                    </>
                  )}
                </div>
              );
            })}
          </div>
        </div>

        {/* Day Detail Panel */}
        <div className="bg-white rounded-3xl shadow-[0_8px_30px_rgb(0,0,0,0.04)] border border-stone-100">
          <div className="px-4 py-4 border-b border-stone-100 flex items-center justify-between">
            <h3 className="font-bold text-sm">
              {selectedDay
                ? `${selectedDay} ${MONTHS[currentMonth]} ${currentYear}`
                : 'Selecciona un día'}
            </h3>
            {selectedDay && (
              <Button size="sm" variant="outline" className="rounded-xl h-8" onClick={() => openNew(selectedDateStr)} data-testid="add-event-day-button">
                <Plus className="w-3.5 h-3.5 mr-1" /> Añadir
              </Button>
            )}
          </div>

          <div className="p-4 space-y-3 max-h-[500px] overflow-y-auto">
            {!selectedDay ? (
              <div className="text-center py-8">
                <CalendarDays className="w-10 h-10 mx-auto mb-3 text-stone-300" />
                <p className="text-sm text-stone-500">Haz clic en un día del calendario</p>
              </div>
            ) : selectedEvents.length === 0 ? (
              <div className="text-center py-8">
                <p className="text-sm text-stone-400 mb-3">Sin eventos este día</p>
                <Button size="sm" className="bg-[#FF6B4A] hover:bg-[#E05A3E] rounded-full shadow-lg shadow-orange-200/50 hover:-translate-y-0.5 transition-all duration-300" onClick={() => openNew(selectedDateStr)}>
                  <Plus className="w-3.5 h-3.5 mr-1" /> Crear evento
                </Button>
              </div>
            ) : (
              selectedEvents.map(ev => {
                const cat = getCategoryInfo(ev.category);
                return (
                  <div key={ev.id} className={`border border-stone-100 rounded-xl p-3 ${ev.completed ? 'opacity-60' : ''}`} data-testid={`event-card-${ev.id}`}>
                    <div className="flex items-start justify-between gap-2">
                      <div className="flex items-start gap-2 min-w-0">
                        <button onClick={() => toggleComplete(ev)} className="mt-0.5 flex-shrink-0" data-testid={`toggle-event-${ev.id}`}>
                          {ev.completed
                            ? <Check className="w-4 h-4 text-green-500" />
                            : <div className="w-4 h-4 border-2 border-stone-300 rounded-xl" />
                          }
                        </button>
                        <div className="min-w-0">
                          <p className={`text-sm font-medium ${ev.completed ? 'line-through text-stone-400' : 'text-stone-900'}`}>{ev.title}</p>
                          <div className="flex items-center gap-2 mt-1 flex-wrap">
                            <span className={`text-[10px] px-1.5 py-0.5 rounded font-medium text-white ${cat.color}`}>{cat.label}</span>
                            {ev.time && <span className="text-[11px] text-stone-500 flex items-center gap-0.5"><Clock className="w-3 h-3" />{ev.time}</span>}
                            {ev.location && <span className="text-[11px] text-stone-500 flex items-center gap-0.5"><MapPin className="w-3 h-3" />{ev.location}</span>}
                          </div>
                          {ev.description && <p className="text-xs text-stone-500 mt-1.5">{ev.description}</p>}
                        </div>
                      </div>
                      <div className="flex gap-1 flex-shrink-0">
                        <button onClick={() => openEdit(ev)} className="p-1 hover:bg-stone-100 rounded" data-testid={`edit-event-${ev.id}`}><Edit2 className="w-3.5 h-3.5 text-stone-400" /></button>
                        <button onClick={() => handleDelete(ev.id)} className="p-1 hover:bg-red-50 rounded" data-testid={`delete-event-${ev.id}`}><Trash2 className="w-3.5 h-3.5 text-red-400" /></button>
                      </div>
                    </div>
                  </div>
                );
              })
            )}
          </div>

          {/* Category Legend */}
          <div className="px-4 py-3 border-t border-stone-100">
            <p className="text-[10px] uppercase tracking-wider text-stone-400 font-bold mb-2">Categorías</p>
            <div className="flex flex-wrap gap-2">
              {CATEGORIES.map(cat => (
                <span key={cat.value} className="flex items-center gap-1 text-[11px] text-stone-600">
                  <span className={`w-2 h-2 rounded-full ${cat.color}`}></span>
                  {cat.label}
                </span>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Event Modal */}
      <Dialog open={modalOpen} onOpenChange={setModalOpen}>
        <DialogContent className="sm:max-w-md" data-testid="event-modal">
          <DialogHeader>
            <DialogTitle>{editingEvent ? 'Editar Evento' : 'Nuevo Evento'}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-2">
            <div>
              <Label htmlFor="event-title">Título *</Label>
              <Input id="event-title" value={form.title} onChange={e => setForm(f => ({ ...f, title: e.target.value }))} placeholder="Ej: Inspección sede central" className="rounded-xl mt-1" data-testid="event-title-input" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label htmlFor="event-date">Fecha *</Label>
                <Input id="event-date" type="date" value={form.date} onChange={e => setForm(f => ({ ...f, date: e.target.value }))} className="rounded-xl mt-1" data-testid="event-date-input" />
              </div>
              <div>
                <Label htmlFor="event-time">Hora</Label>
                <Input id="event-time" type="time" value={form.time} onChange={e => setForm(f => ({ ...f, time: e.target.value }))} className="rounded-xl mt-1" data-testid="event-time-input" />
              </div>
            </div>
            <div>
              <Label>Categoría</Label>
              <Select value={form.category} onValueChange={v => setForm(f => ({ ...f, category: v }))}>
                <SelectTrigger className="rounded-xl mt-1" data-testid="event-category-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {CATEGORIES.map(cat => (
                    <SelectItem key={cat.value} value={cat.value}>
                      <span className="flex items-center gap-2">
                        <span className={`w-2.5 h-2.5 rounded-full ${cat.color}`}></span>
                        {cat.label}
                      </span>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label htmlFor="event-location">Ubicación</Label>
              <Input id="event-location" value={form.location} onChange={e => setForm(f => ({ ...f, location: e.target.value }))} placeholder="Ej: Juzgado Social N.3, Sevilla" className="rounded-xl mt-1" data-testid="event-location-input" />
            </div>
            <div>
              <Label htmlFor="event-description">Descripción</Label>
              <Textarea id="event-description" value={form.description} onChange={e => setForm(f => ({ ...f, description: e.target.value }))} placeholder="Detalles adicionales..." className="rounded-xl mt-1" rows={3} data-testid="event-description-input" />
            </div>
            <div className="flex items-center gap-2">
              <Checkbox id="event-reminder" checked={form.reminder} onCheckedChange={v => setForm(f => ({ ...f, reminder: v }))} data-testid="event-reminder-checkbox" />
              <Label htmlFor="event-reminder" className="text-sm cursor-pointer">Mostrar en alertas del Dashboard (próximos 7 días)</Label>
            </div>
            <div className="flex gap-3 pt-2">
              <Button className="flex-1 bg-[#FF6B4A] hover:bg-[#E05A3E] rounded-full shadow-lg shadow-orange-200/50 hover:-translate-y-0.5 transition-all duration-300" onClick={handleSave} disabled={saving} data-testid="save-event-button">
                {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
                {editingEvent ? 'Guardar cambios' : 'Crear evento'}
              </Button>
              <Button variant="outline" className="rounded-xl" onClick={() => setModalOpen(false)}>Cancelar</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default CalendarPage;
